import { useState, useEffect } from 'react'
import {
  Card, Button, Modal, List, Space, message, Spin, Empty,
  Tooltip, Tag, Typography, Alert, Descriptions, Switch, Select, Divider,
} from 'antd'
import {
  SaveOutlined, DeleteOutlined, ReloadOutlined,
  DownloadOutlined, RollbackOutlined, ExclamationCircleOutlined,
  DatabaseOutlined, CheckCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons'
import axiosInstance from '@/lib/axios'
import dayjs from 'dayjs'

const { Text } = Typography

const INTERVAL_OPTIONS = [
  { value: 6, label: 'Ogni 6 ore' },
  { value: 12, label: 'Ogni 12 ore' },
  { value: 24, label: 'Ogni 24 ore (giornaliero)' },
  { value: 48, label: 'Ogni 48 ore' },
  { value: 168, label: 'Settimanale' },
]

interface BackupFile {
  filename: string
  size_bytes: number
  size_mb: number
  created_at: string
  modified_at: string
  is_pre_restore: boolean
}

export const DatabaseBackupManager = () => {
  const [backups, setBackups] = useState<BackupFile[]>([])
  const [loading, setLoading] = useState(false)
  const [creatingBackup, setCreatingBackup] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [restoreTarget, setRestoreTarget] = useState<BackupFile | null>(null)

  // Schedule state
  const [schedule, setSchedule] = useState({ enabled: false, interval_hours: 24, last_run_at: null as string | null, last_run_status: null as string | null, last_run_message: null as string | null, next_run_at: null as string | null })
  const [savingSchedule, setSavingSchedule] = useState(false)
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [scheduleInterval, setScheduleInterval] = useState(24)

  const loadSchedule = async () => {
    try {
      const res = await axiosInstance.get('/database/schedule')
      setSchedule(res.data)
      setScheduleEnabled(res.data.enabled)
      setScheduleInterval(res.data.interval_hours)
    } catch { /* non bloccare se fallisce */ }
  }

  const saveSchedule = async () => {
    setSavingSchedule(true)
    try {
      const res = await axiosInstance.put('/database/schedule', { enabled: scheduleEnabled, interval_hours: scheduleInterval })
      setSchedule(prev => ({ ...prev, ...res.data }))
      message.success(scheduleEnabled ? `Backup automatico attivato (ogni ${scheduleInterval}h)` : 'Backup automatico disattivato')
    } catch (err: any) {
      message.error(err.response?.data?.detail || 'Errore salvataggio pianificazione')
    } finally {
      setSavingSchedule(false)
    }
  }

  const loadBackups = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.get('/database/backups')
      setBackups(res.data.backups || [])
    } catch {
      message.error('Errore nel caricamento dei backup')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadBackups(); loadSchedule() }, [])

  const handleCreateBackup = async () => {
    setCreatingBackup(true)
    try {
      const res = await axiosInstance.post('/database/backup')
      message.success(`Backup creato: ${res.data.filename} (${res.data.size_mb} MB)`)
      await loadBackups()
    } catch (err: any) {
      message.error(err.response?.data?.detail || 'Errore durante il backup')
    } finally {
      setCreatingBackup(false)
    }
  }

  const handleDownload = (filename: string) => {
    const token = localStorage.getItem('garage_access_token')
    const url = `${axiosInstance.defaults.baseURL}/database/backups/${filename}/download`
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    // Use fetch to attach auth header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const objUrl = URL.createObjectURL(blob)
        a.href = objUrl
        a.click()
        URL.revokeObjectURL(objUrl)
      })
      .catch(() => message.error('Errore download'))
  }

  const handleDelete = (backup: BackupFile) => {
    Modal.confirm({
      title: 'Elimina backup',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Eliminare <Text code>{backup.filename}</Text>?</p>
          <Text type="secondary" style={{ fontSize: 12 }}>Questa operazione è irreversibile.</Text>
        </div>
      ),
      okText: 'Elimina',
      okButtonProps: { danger: true },
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await axiosInstance.delete(`/database/backups/${backup.filename}`)
          message.success('Backup eliminato')
          await loadBackups()
        } catch (err: any) {
          message.error(err.response?.data?.detail || 'Errore eliminazione')
        }
      },
    })
  }

  const handleRestore = async () => {
    if (!restoreTarget) return
    setRestoring(true)
    try {
      const res = await axiosInstance.post(`/database/restore/${restoreTarget.filename}`)
      message.success({
        content: (
          <span>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            Ripristino completato. Backup di sicurezza: <Text code>{res.data.pre_restore_backup}</Text>
          </span>
        ),
        duration: 6,
      })
      setRestoreTarget(null)
      await loadBackups()
    } catch (err: any) {
      message.error(err.response?.data?.detail || 'Errore durante il ripristino')
    } finally {
      setRestoring(false)
    }
  }

  const regularBackups = backups.filter(b => !b.is_pre_restore)
  const preRestoreBackups = backups.filter(b => b.is_pre_restore)

  const renderList = (items: BackupFile[], isPreRestore = false) => (
    <List
      dataSource={items}
      renderItem={(backup) => (
        <List.Item
          key={backup.filename}
          style={{ padding: '10px 12px', borderRadius: 6, marginBottom: 6, border: '1px solid #f0f0f0' }}
          actions={[
            <Tooltip title="Scarica sul tuo computer" key="dl">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(backup.filename)}
              />
            </Tooltip>,
            !isPreRestore && (
              <Tooltip title="Ripristina database a questo punto" key="restore">
                <Button
                  size="small"
                  icon={<RollbackOutlined />}
                  onClick={() => setRestoreTarget(backup)}
                >
                  Ripristina
                </Button>
              </Tooltip>
            ),
            <Tooltip title="Elimina" key="del">
              <Button
                size="small"
                danger
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(backup)}
              />
            </Tooltip>,
          ].filter(Boolean)}
        >
          <List.Item.Meta
            title={
              <Space size={6} wrap>
                <Text code style={{ fontSize: 13 }}>{backup.filename}</Text>
                <Tag color={isPreRestore ? 'orange' : 'blue'}>{backup.size_mb} MB</Tag>
                {isPreRestore && <Tag color="warning">pre-restore</Tag>}
              </Space>
            }
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(backup.created_at).format('DD/MM/YYYY HH:mm:ss')}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  )

  return (
    <>
      <Card
        title={<Space><DatabaseOutlined />Backup e Ripristino Database</Space>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        {/* DB Info */}
        <Descriptions size="small" column={3} style={{ marginBottom: 16 }} bordered>
          <Descriptions.Item label="Database">PostgreSQL 16</Descriptions.Item>
          <Descriptions.Item label="Host">postgres:5432 (Docker)</Descriptions.Item>
          <Descriptions.Item label="Cartella server">/var/backups/garage-management</Descriptions.Item>
          <Descriptions.Item label="Formato backup">pg_dump -Fc (binario compresso)</Descriptions.Item>
        </Descriptions>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="I backup vengono salvati in /var/backups/garage-management sul server, fuori da Docker. Accessibili via SSH/SFTP. Si consiglia di scaricare una copia sul proprio computer periodicamente."
        />

        {/* Create backup */}
        <Space style={{ marginBottom: 20 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={creatingBackup}
            onClick={handleCreateBackup}
            size="middle"
          >
            {creatingBackup ? 'Creazione in corso…' : 'Crea Backup Ora'}
          </Button>
          <Tooltip title="Aggiorna lista">
            <Button icon={<ReloadOutlined />} onClick={loadBackups} loading={loading} />
          </Tooltip>
          {regularBackups.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {regularBackups.length} backup disponibili
            </Text>
          )}
        </Space>

        <Spin spinning={loading}>
          {backups.length === 0 ? (
            <Empty description="Nessun backup disponibile" imageStyle={{ height: 48 }} />
          ) : (
            <>
              {renderList(regularBackups)}

              {preRestoreBackups.length > 0 && (
                <details style={{ marginTop: 12 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: '#999', userSelect: 'none' }}>
                    {preRestoreBackups.length} backup di sicurezza pre-restore
                  </summary>
                  <div style={{ marginTop: 8 }}>
                    {renderList(preRestoreBackups, true)}
                  </div>
                </details>
              )}
            </>
          )}
        </Spin>
      </Card>

      {/* Schedule section */}
      <Card
        title={<Space><ClockCircleOutlined />Backup Automatico Pianificato</Space>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <Space wrap>
            <Switch
              checked={scheduleEnabled}
              onChange={setScheduleEnabled}
              checkedChildren="Attivo"
              unCheckedChildren="Disattivato"
            />
            <Select
              value={scheduleInterval}
              onChange={setScheduleInterval}
              style={{ width: 220 }}
              options={INTERVAL_OPTIONS}
              disabled={!scheduleEnabled}
            />
            <Button type="primary" onClick={saveSchedule} loading={savingSchedule}>
              Salva pianificazione
            </Button>
          </Space>

          {schedule.enabled && (
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Prossimo backup">
                {schedule.next_run_at ? dayjs(schedule.next_run_at).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Ultimo backup automatico">
                {schedule.last_run_at ? dayjs(schedule.last_run_at).format('DD/MM/YYYY HH:mm') : 'Mai eseguito'}
              </Descriptions.Item>
              {schedule.last_run_status && (
                <Descriptions.Item label="Esito ultimo">
                  <Tag color={schedule.last_run_status === 'success' ? 'green' : 'red'}>
                    {schedule.last_run_status === 'success' ? 'Successo' : 'Errore'}
                  </Tag>
                  {schedule.last_run_status === 'error' && schedule.last_run_message && (
                    <Text type="danger" style={{ fontSize: 12, marginLeft: 8 }}>{schedule.last_run_message}</Text>
                  )}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </Space>
      </Card>

      <Divider style={{ margin: '8px 0 16px' }} />

      {/* Restore confirmation modal */}
      <Modal
        title={<Space><ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />Ripristina Database</Space>}
        open={!!restoreTarget}
        onCancel={() => !restoring && setRestoreTarget(null)}
        closable={!restoring}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={() => setRestoreTarget(null)} disabled={restoring}>
            Annulla
          </Button>,
          <Button key="ok" danger loading={restoring} onClick={handleRestore}>
            {restoring ? 'Ripristino in corso…' : 'Ripristina'}
          </Button>,
        ]}
      >
        <Alert
          type="error"
          showIcon
          message="Operazione irreversibile"
          description="Tutti i dati attuali verranno sostituiti con quelli del backup selezionato. Un backup di sicurezza verrà creato automaticamente prima del ripristino."
          style={{ marginBottom: 16 }}
        />
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Backup selezionato">
            <Text code>{restoreTarget?.filename}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Data backup">
            {restoreTarget ? dayjs(restoreTarget.created_at).format('DD/MM/YYYY HH:mm:ss') : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Dimensione">
            {restoreTarget?.size_mb} MB
          </Descriptions.Item>
        </Descriptions>
      </Modal>
    </>
  )
}

export default DatabaseBackupManager
